import React from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { IconButton, Typography } from "@mui/material";
import { Box, Grid } from "@mui/system";
import DownloadIcon from "@mui/icons-material/Download";
import ConversionsUploadTable from "./ConversionsUploadTable";
import {
  selectBizBeAwareConversionsUploadLoadedRequestKey,
  selectBizBeAwareConversionsUploadLoading,
  selectBizBeAwareConversionsUploadOutput,
} from "../../../../../../../features/bizDevelopmentDashboardSlice";
import { buildBeAwareRequestKey } from "../../../../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardEndPointLogic";
import { useBizDevelopmentDashboard } from "../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";
import { exportBeAwareTableToCSV } from "../../beAwareCsvHelpers";
import type { BeAwareTableRef } from "../FraudPlayers/FraudPlayers";

const tableHeadings = {
  upload_date: "Upload Date",
  loaded_ftd: "Loaded FTD",
  loaded_ftd_of_total_exc_incent: "% Loaded FTD of Total FTD except Incent",
  total_ftd: "Total FTD",
  loaded_qftd: "Loaded QFTD",
  total_qftd: "Total QFTD",
  brand_total_ftd: "Brand Total FTD",
  players_total_ftd: "Players Total FTD",
  players_total_ftd_without_incent: "Players Total FTD Without Incent",
  incent_ftd: "Incent FTD",
  lack_of_player_report: "Lack of Player Report",
  direct_ftd: "Direct FTD",
  gbraid_or_wbraid: "Gbraid or Wbraid",
  paid_no_click_id: "Paid no Click ID",
  problematic_ftd: "Problematic FTD",
  multiple_ftds: "Multiple FTDs",
  eligible_with_exact_click_id: "Eligible with Exact Click ID",
  total_loaded_success: "Total Loaded Success",
  loaded_success_to_google: "Loaded Success to Google",
  loaded_success_to_bing: "Loaded Success to Bing",
  abs_late_ftd_succeeded: "Abs late FTD Succeeded",
  total_loaded_failure: "Total Loaded Failure",
  percent_late_ftd: "Percent Late FTD",
  this_click_is_too_old: "This Click is Too Old",
};

// Keep raw values; empty string when missing so the table shows "N/A".
const raw = (value: any) =>
  value === null || value === undefined ? "" : String(value);

// Formats an API date (e.g. "2026-07-11") to "dd-mm-yyyy", or "" when invalid.
const formatDate = (value: any) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : String(value);
};

// Calculated in client: (numerator / denominator) * 100 as a number, or null.
const percentValue = (numerator: any, denominator: any) => {
  const n = Number(numerator);
  const d = Number(denominator);
  if (!d || Number.isNaN(n) || Number.isNaN(d)) return null;
  return (n / d) * 100;
};

// Converts an API fraction (e.g. 0.857...) to a percentage number, or null.
const fractionToPercent = (value: any) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return n * 100;
};

// Converts an API fraction to a rounded percentage label (e.g. "85%"), or "".
const fractionToPercentLabel = (value: any) => {
  const percent = fractionToPercent(value);
  return percent === null ? "" : `${Math.round(percent)}%`;
};

// CSV cell resolver: mirror the merged/badge columns as text.
const getConversionsCellValue = (key: string, row: any) => {
  if (key === "loaded_ftd" || key === "loaded_qftd") {
    const count = row[key];
    const percent =
      key === "loaded_ftd" ? row.loaded_ftd_of_total : row.loaded_qftd_of_total;
    if (count === "" || count === null || count === undefined) return "";
    return percent === null || percent === undefined
      ? String(count)
      : `${count} (${Math.round(percent)}%)`;
  }
  if (key === "loaded_ftd_of_total_exc_incent") {
    const percent = row[key];
    return percent === null || percent === undefined
      ? ""
      : `${Math.round(percent)}%`;
  }
  return row[key] ?? "";
};

const ConversionsUpload = React.forwardRef<BeAwareTableRef>((_, ref) => {
  const isLoading = useSelector(selectBizBeAwareConversionsUploadLoading);
  const output = useSelector(selectBizBeAwareConversionsUploadOutput);
  const loadedRequestKey = useSelector(
    selectBizBeAwareConversionsUploadLoadedRequestKey,
  );
  const { fetchBeAwareConversionsUploadData, getSitesFromParams } =
    useBizDevelopmentDashboard();

  React.useEffect(() => {
    const sites = getSitesFromParams();
    if (sites.length === 0) return;
    if (loadedRequestKey === buildBeAwareRequestKey(sites)) return;
    fetchBeAwareConversionsUploadData(sites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchBeAwareConversionsUploadData(getSitesFromParams());
    },
  }));

  const rows = React.useMemo(
    () =>
      (output ?? []).map((item: Record<string, any>) => ({
        upload_date: formatDate(item.brand_action_date),
        loaded_ftd: raw(item.FTD_loaded),
        loaded_ftd_of_total: percentValue(item.FTD_loaded, item.brand_total_ftd),
        loaded_ftd_of_total_exc_incent: fractionToPercent(
          item.loaded_from_total_exc_incent,
        ),
        total_ftd: raw(item.brand_total_ftd),
        loaded_qftd: raw(item.QFTD_loaded),
        loaded_qftd_of_total: percentValue(item.QFTD_loaded, item.Total_Qftd),
        total_qftd: raw(item.Total_Qftd),
        brand_total_ftd: raw(item.brand_total_ftd),
        players_total_ftd: raw(item.players_total_ftd),
        players_total_ftd_without_incent: raw(
          item.players_total_ftd_without_incent,
        ),
        incent_ftd: raw(item.incent_ftd),
        lack_of_player_report: raw(item.lack_of_player_report),
        direct_ftd: raw(item.direct_ftd),
        gbraid_or_wbraid: raw(item.gbraid_or_wbraid),
        paid_no_click_id: raw(item.Paid_no_Click_Id),
        problematic_ftd: raw(item.problematic_ftd),
        multiple_ftds: raw(item.multiple_ftds),
        eligible_with_exact_click_id: raw(item.eligible_with_exact_click_id),
        total_loaded_success: raw(item.total_loaded_success),
        loaded_success_to_google: raw(item.loaded_success_to_google),
        loaded_success_to_bing: raw(item.loaded_success_to_bing),
        abs_late_ftd_succeeded: raw(item.Abs_late_ftd_succeeded),
        total_loaded_failure: raw(item.total_loaded_failure),
        percent_late_ftd: fractionToPercentLabel(item.Percent_Late_ftd),
        this_click_is_too_old: raw(item.This_click_is_too_old),
      })),
    [output],
  );

  const handleDownload = () => {
    exportBeAwareTableToCSV({
      tableHeadings,
      rows,
      filename: "conversions_upload",
      getCellValue: getConversionsCellValue,
    });
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Grid container spacing={2} alignItems="center" px={2} pb={2}>
          <Grid size={6}>
            <Typography fontSize={"18px"} mr={2}>
              Conversions Upload in MTD
            </Typography>
            <Typography fontSize={"12px"} color={"#00000099"} mr={2}>
              The Last 2 days are excluded from this review, as data isn't
              reflected in real-time in the Advertising Platforms
            </Typography>
          </Grid>
          <Grid size={6}>
            <Box display={"flex"} justifyContent={"flex-end"}>
              <IconButton
                aria-label="download"
                color="secondary"
                sx={{ ml: 2 }}
                disabled={isLoading || rows.length === 0}
                onClick={handleDownload}
                data-tracker="857"
              >
                <DownloadIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <ConversionsUploadTable
        tableHeadings={tableHeadings}
        rows={rows}
        isLoading={isLoading}
      />
    </>
  );
});

export default ConversionsUpload;
